import { BigBashEvent } from "@/types/types";
import { logError } from "@lib/logger";

const EVENT_TIME_ZONE = 'Europe/Berlin';
const DATE_STRING_HAS_EXPLICIT_OFFSET_PATTERN = /Z$|[+-]\d{2}:\d{2}$/;

export class TypeConversionManager {
	/**
	 * Converts an event object to valid types: datetime from string to Date,
	 * duration from string to number, and filters out empty teams.
	 * @param eventData The event data to convert.
	 * @returns The converted event data.
	 */
	public convertEventObjectToValidTypes(eventData: BigBashEvent): BigBashEvent {
		const converted = { ...eventData };
		converted.datetime = this.convertStringToDate(converted.datetime as unknown as string);
		converted.duration = this._convertDurationToNumber(converted.duration as unknown as string);
		converted.teams = this._filterEmptyTeams(converted.teams);
		return converted as BigBashEvent;
	}

	public convertStringToDate(date: string): Date {
		if (typeof date !== 'string') {
			return date as Date;
		}
		if (DATE_STRING_HAS_EXPLICIT_OFFSET_PATTERN.test(date)) {
			return new Date(date);
		}
		return this._convertWallClockStringToDate(date, EVENT_TIME_ZONE);
	}

	public convertDateToHTMLInputValue(date: Date): string {
		if (Number.isNaN(date.getTime())) {
			logError('Expected a valid Date for the datetime-local input', {
				received_value: String(date),
			});
			return '';
		}
		return date
			.toLocaleString('sv-SE', { timeZone: EVENT_TIME_ZONE })
			.replace(' ', 'T')
			.slice(0, 16);
	}

	/** Interprets a timezone-less "YYYY-MM-DDTHH:mm" string (what a datetime-local input returns) as wall-clock time in the given timeZone, independent of the host machine's own timezone. */
	private _convertWallClockStringToDate(wallClockString: string, timeZone: string): Date {
		const [datePart, timePart] = wallClockString.split('T');
		const [year, month, day] = datePart.split('-').map(Number);
		const [hour, minute] = (timePart ?? '00:00').split(':').map(Number);
		const assumedUTC = new Date(Date.UTC(year, month - 1, day, hour, minute));
		const offsetMinutes = this._getTimeZoneOffsetMinutes(assumedUTC, timeZone);
		return new Date(assumedUTC.getTime() - offsetMinutes * 60_000);
	}

	private _getTimeZoneOffsetMinutes(date: Date, timeZone: string): number {
		const dateTimeFormat = new Intl.DateTimeFormat('en-US', {
			timeZone,
			hourCycle: 'h23',
			year: 'numeric',
			month: '2-digit',
			day: '2-digit',
			hour: '2-digit',
			minute: '2-digit',
			second: '2-digit',
		});
		const partsByType = dateTimeFormat.formatToParts(date).reduce((accumulatedParts: Record<string, string>, part) => {
			accumulatedParts[part.type] = part.value;
			return accumulatedParts;
		}, {});
		const wallClockAsUTC = Date.UTC(
			Number(partsByType.year), Number(partsByType.month) - 1, Number(partsByType.day),
			Number(partsByType.hour), Number(partsByType.minute), Number(partsByType.second)
		);
		return (wallClockAsUTC - date.getTime()) / 60_000;
	}

	private _convertDurationToNumber(duration: string): number {
		if (typeof duration !== 'string' && typeof duration !== 'number') {
			throw new Error('Invalid duration format');
		}
		const parsedDuration = parseInt(duration, 10);
		if (isNaN(parsedDuration)) {
			throw new Error('Invalid duration value');
		}
		return parsedDuration;
	}

	private _filterEmptyTeams(teams: any[]): any[] {
		return teams.filter(team => team?.name && Array.isArray(team?.players));
	}
}

export const typeConversionManager = new TypeConversionManager();